import React, { useState } from "react";
import { Alert } from "../../types";
import { CheckCircle, AlertTriangle } from "lucide-react";

interface AlertListTableProps {
  alerts: (Alert & { orgName?: string; siteName?: string })[];
  onResolveAlert: (alertId: string) => void;
  onBatchResolveAlerts: (alertIds: string[]) => void;
  loading: boolean;
}

const AlertListTable: React.FC<AlertListTableProps> = ({
  alerts,
  onResolveAlert,
  onBatchResolveAlerts,
  loading,
}) => {
  const [selectedAlerts, setSelectedAlerts] = useState<Set<string>>(new Set());
  const [isBatchResolving, setIsBatchResolving] = useState(false);

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString();
  };

  const getStatusBadge = (status: string) => {
    if (status === "active") {
      return (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
          <AlertTriangle className="w-3 h-3 mr-1" />
          Active
        </span>
      );
    } else {
      return (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
          <CheckCircle className="w-3 h-3 mr-1" />
          Resolved
        </span>
      );
    }
  };

  // 获取所有活跃的告警
  const activeAlerts = alerts.filter((alert) => alert.status === "active");

  // 处理单个告警选择
  const handleAlertSelect = (alertId: string) => {
    const newSelected = new Set(selectedAlerts);
    if (newSelected.has(alertId)) {
      newSelected.delete(alertId);
    } else {
      newSelected.add(alertId);
    }
    setSelectedAlerts(newSelected);
  };

  // 处理全选/取消全选
  const handleSelectAll = () => {
    if (selectedAlerts.size === activeAlerts.length) {
      setSelectedAlerts(new Set());
    } else {
      setSelectedAlerts(new Set(activeAlerts.map((alert) => alert.id)));
    }
  };

  // 处理批量解决
  const handleBatchResolve = async () => {
    if (selectedAlerts.size === 0) return;

    setIsBatchResolving(true);
    try {
      await onBatchResolveAlerts(Array.from(selectedAlerts));
      setSelectedAlerts(new Set()); // 清空选择
    } catch (error) {
      console.error("Failed to batch resolve alerts:", error);
    } finally {
      setIsBatchResolving(false);
    }
  };

  if (loading) {
    return (
      <div className="p-6">
        <div className="flex items-center justify-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          <span className="ml-2 text-gray-600">Loading alerts...</span>
        </div>
      </div>
    );
  }

  if (alerts.length === 0) {
    return (
      <div className="p-6">
        <div className="text-center py-8">
          <AlertTriangle className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">
            No alerts found
          </h3>
          <p className="mt-1 text-sm text-gray-500">
            There are no alerts matching your current filters.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="pt-2 px-6 pb-4">
      {/* 批量操作工具栏 */}
      {activeAlerts.length > 0 && (
        <div className="mb-4 p-4 bg-gray-50 rounded-lg border border-gray-200">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <label className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  checked={
                    selectedAlerts.size === activeAlerts.length &&
                    activeAlerts.length > 0
                  }
                  onChange={handleSelectAll}
                  className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                <span className="text-sm text-gray-700">
                  Select All ({activeAlerts.length} active alerts)
                </span>
              </label>
              {selectedAlerts.size > 0 && (
                <span className="text-sm text-gray-600">
                  {selectedAlerts.size} selected
                </span>
              )}
            </div>
            {selectedAlerts.size > 0 && (
              <button
                onClick={handleBatchResolve}
                disabled={isBatchResolving}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
              >
                {isBatchResolving ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    <span>Resolving...</span>
                  </>
                ) : (
                  <>
                    <CheckCircle className="w-4 h-4" />
                    <span>Resolve Selected ({selectedAlerts.size})</span>
                  </>
                )}
              </button>
            )}
          </div>
        </div>
      )}

      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                <input
                  type="checkbox"
                  checked={
                    selectedAlerts.size === activeAlerts.length &&
                    activeAlerts.length > 0
                  }
                  onChange={handleSelectAll}
                  className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
              </th>
              <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Organization
              </th>
              <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Site
              </th>
              <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Alert
              </th>
              <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Device
              </th>
              <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Values
              </th>
              <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Status
              </th>
              <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Timestamp
              </th>
              <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody>
            {alerts.map((alert) => (
              <tr
                key={alert.id}
                className={selectedAlerts.has(alert.id) ? "bg-blue-50" : ""}
              >
                <td className="px-6 py-4 whitespace-nowrap">
                  {alert.status === "active" && (
                    <input
                      type="checkbox"
                      checked={selectedAlerts.has(alert.id)}
                      onChange={() => handleAlertSelect(alert.id)}
                      className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                  )}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-gray-800 text-sm font-normal">
                  {alert.organization?.name || "-"}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-gray-800 text-sm font-normal">
                  {alert.site?.name || "-"}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-gray-800 text-sm font-normal">
                  {alert.rule_name}
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-gray-800 text-sm font-normal">
                    {alert.device_name}
                  </div>
                  <div className="text-xs text-gray-500">
                    {alert.device_mac}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-gray-800 text-sm font-normal">
                    Triggered: {alert.triggered_value}
                  </div>
                  <div className="text-xs text-gray-500">
                    Threshold: {alert.threshold_value}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  {getStatusBadge(alert.status)}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  {formatDate(alert.device_timestamp_utc)}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                  {alert.status === "active" && (
                    <button
                      onClick={() => onResolveAlert(alert.id)}
                      className="text-indigo-600 hover:text-indigo-900 bg-indigo-50 hover:bg-indigo-100 px-3 py-1 rounded-md text-sm font-medium transition-colors"
                    >
                      Resolve
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default AlertListTable;
